import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { api } from "../lib/api";

interface Item {
  oracle_id: string;
  full_name: string;
  nationality: string | null;
  document: string;
  expires_at: string;
  days_left: number;
  expired: boolean;
}

export default function DocumentsPage() {
  const q = useQuery({
    queryKey: ["expiring-docs"],
    queryFn: () => api.get<{ items: Item[]; count: number }>("/api/org/expiring-documents?within_days=180"),
  });

  if (q.isPending) {
    return <div className="text-slate-400 flex-1 min-h-0">Loading…</div>;
  }
  if (!q.data) {
    return <div className="text-red-300 flex-1 min-h-0">Failed to load.</div>;
  }

  return (
    <div className="flex flex-col gap-3 sm:gap-4 w-full min-h-0 flex-1">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-100">
          Expiring documents
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          Passports &amp; residency cards expiring in the next 180 days (or already
          expired).
        </p>
      </div>

      {q.data.items.length === 0 ? (
        <div className="rounded-xl bg-[var(--color-surface)]/60 ring-1 ring-[var(--color-border)] p-6 text-slate-400 text-sm text-center">
          ✓ Nothing expires in the next 180 days.
        </div>
      ) : (
        <div
          className={
            "rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] " +
            "divide-y divide-slate-800 overflow-y-auto overscroll-y-contain " +
            "max-h-[min(720px,calc(100dvh-10.5rem))] sm:max-h-[calc(100dvh-11rem)] " +
            "[-webkit-overflow-scrolling:touch]"
          }
        >
          {q.data.items.map((it, idx) => (
            <div key={idx} className="p-3 flex items-center gap-3 flex-wrap sm:flex-nowrap">
              {(it.expired || it.days_left < 30) && (
                <AlertTriangle size={14} className={it.expired ? "text-red-400" : "text-amber-400"} />
              )}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/employees/${encodeURIComponent(it.oracle_id)}`}
                  className="text-slate-100 hover:text-sky-200"
                >
                  {it.full_name}
                </Link>
                <div className="text-xs text-slate-500">
                  {it.nationality || "—"} · {it.document}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className={`text-sm tabular-nums ${
                    it.expired ? "text-red-300" : it.days_left < 30 ? "text-amber-300" : "text-slate-300"
                  }`}
                >
                  {it.expired
                    ? "Expired"
                    : it.days_left === 0
                    ? "today"
                    : `${it.days_left}d`}
                </div>
                <div className="text-xs text-slate-500">
                  {new Date(it.expires_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
